import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useLang } from "@/i18n";
import { CreditCard, Check } from "lucide-react";
import { toast } from "sonner";

export default function Checkout() {
  const { items, subtotal } = useCart();
  const { t, tl, lang } = useLang();
  const { format, currency } = useCurrency();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ full_name: "", address: "", city: "", postal_code: "", country: "Schweiz / Suisse", phone: "" });

  const [shippingMethods, setShippingMethods] = useState([]);
  const [selectedShip, setSelectedShip] = useState("");
  const [promo, setPromo] = useState("");
  const [discount, setDiscount] = useState({ percent: 0, amount: 0, applied: false });

  useEffect(() => {
    api.get("/shipping").then((res) => {
      setShippingMethods(res.data);
      if (res.data.length) setSelectedShip(res.data[0].id);
    });
  }, []);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const shipCostOf = (m) => (m.free_over && subtotal >= m.free_over ? 0 : m.price);
  const selectedMethod = shippingMethods.find((m) => m.id === selectedShip);
  const shippingCost = selectedMethod ? shipCostOf(selectedMethod) : 0;
  const discountAmount = discount.applied ? subtotal * discount.percent / 100 : 0;
  const total = Math.max(0, subtotal - discountAmount) + shippingCost;

  const applyPromo = async () => {
    if (!promo.trim()) return;
    try {
      const { data } = await api.post("/discounts/validate", { code: promo, subtotal });
      if (data.valid) {
        setDiscount({ percent: data.percent, amount: data.discount_amount, applied: true });
        toast.success(`${t("applied")} · -${data.percent}%`);
      } else {
        setDiscount({ percent: 0, amount: 0, applied: false });
        toast.error(t("invalid_code"));
      }
    } catch {
      toast.error(t("invalid_code"));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/session", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        shipping: form,
        origin_url: window.location.origin,
        language: lang,
        currency,
        discount_code: discount.applied ? promo : "",
        shipping_method_id: selectedShip,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  const fields = [
    { name: "full_name", label: t("full_name") },
    { name: "address", label: t("address") },
    { name: "city", label: t("city") },
    { name: "postal_code", label: t("postal_code") },
    { name: "country", label: t("country") },
    { name: "phone", label: t("phone") },
  ];

  if (items.length === 0) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
        <p className="text-[#6B7280]">{t("cart_empty")}</p>
        <button onClick={() => navigate("/shop")} className="text-xs tracking-luxe uppercase border-b border-[#1A1F2C] pb-1">{t("cart_continue")}</button>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-8 lg:px-16 py-12 lg:py-16 fade-up">
      <h1 className="font-serif-display text-4xl sm:text-5xl text-[#1A1F2C] text-center mb-12">{t("checkout_title")}</h1>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-5xl mx-auto">
        <form onSubmit={submit} data-testid="checkout-form" className="space-y-5">
          <h2 className="text-xs tracking-luxe uppercase text-[#C5A880] mb-2">{t("checkout_shipping")}</h2>
          {fields.map((f) => (
            <div key={f.name}>
              <label className="text-xs tracking-luxe uppercase text-[#6B7280] block mb-2">{f.label}</label>
              <input
                name={f.name}
                value={form[f.name]}
                onChange={onChange}
                required={f.name !== "phone"}
                data-testid={`checkout-${f.name}`}
                className="w-full bg-white border border-[#EAE6E1] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A880] transition-colors"
              />
            </div>
          ))}

          <div>
            <label className="text-xs tracking-luxe uppercase text-[#6B7280] block mb-3">{t("choose_shipping")}</label>
            <div className="space-y-2">
              {shippingMethods.map((m) => {
                const cost = shipCostOf(m);
                return (
                  <button type="button" key={m.id} data-testid={`shipping-option-${m.id}`}
                    onClick={() => setSelectedShip(m.id)}
                    className={`w-full flex items-center justify-between border px-4 py-3 text-sm transition-colors ${selectedShip === m.id ? "border-[#C5A880] bg-[#F0EAE1]" : "border-[#EAE6E1] bg-white hover:border-[#C5A880]"}`}>
                    <span className="flex items-center gap-2">
                      {selectedShip === m.id && <Check className="w-4 h-4 text-[#C5A880]" />}
                      {tl(m.name)}
                    </span>
                    <span>{cost === 0 ? t("free") : format(cost)}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="checkout-pay-button"
            className="w-full bg-[#1A1F2C] text-white text-xs tracking-luxe uppercase py-4 hover:bg-[#C5A880] transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
          >
            <CreditCard className="w-4 h-4" strokeWidth={1.5} />
            {loading ? t("processing") : t("pay_now")}
          </button>
          <p className="text-xs text-[#6B7280] text-center">{t("payment_methods_note")}</p>
        </form>

        <div className="bg-white border border-[#EAE6E1] p-8 h-fit">
          <h2 className="text-xs tracking-luxe uppercase text-[#C5A880] mb-6">{t("checkout_summary")}</h2>
          <div className="space-y-4 mb-6">
            {items.map((i) => (
              <div key={i.product_id} className="flex gap-4">
                <div className="w-14 h-16 bg-[#F0EAE1] overflow-hidden shrink-0">
                  <img src={i.image} alt={tl(i.name)} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1">
                  <p className="font-serif-display text-base text-[#1A1F2C] leading-tight">{tl(i.name)}</p>
                  <p className="text-xs text-[#6B7280]">× {i.quantity}</p>
                </div>
                <span className="text-sm text-[#1A1F2C]">{format(i.price * i.quantity)}</span>
              </div>
            ))}
          </div>

          {/* Promo code */}
          <div className="flex gap-2 mb-5">
            <input value={promo} onChange={(e) => setPromo(e.target.value)} placeholder={t("promo_code")} data-testid="promo-input"
              className="flex-1 bg-[#FAF9F6] border border-[#EAE6E1] px-3 py-2.5 text-sm uppercase focus:outline-none focus:border-[#C5A880]" />
            <button type="button" onClick={applyPromo} data-testid="promo-apply"
              className="text-xs tracking-luxe uppercase border border-[#1A1F2C] px-4 hover:bg-[#1A1F2C] hover:text-white transition-colors">
              {t("apply")}
            </button>
          </div>

          <div className="space-y-2 border-t border-[#EAE6E1] pt-4 text-sm">
            <div className="flex justify-between text-[#6B7280]">
              <span>{t("cart_subtotal")}</span><span data-testid="summary-subtotal">{format(subtotal)}</span>
            </div>
            {discount.applied && (
              <div className="flex justify-between text-[#166534]" data-testid="summary-discount">
                <span>{t("discount")} (-{discount.percent}%)</span><span>− {format(discountAmount)}</span>
              </div>
            )}
            <div className="flex justify-between text-[#6B7280]">
              <span>{t("shipping")}</span><span data-testid="summary-shipping">{shippingCost === 0 ? t("free") : format(shippingCost)}</span>
            </div>
          </div>

          <div className="border-t border-[#EAE6E1] mt-4 pt-4 flex justify-between items-center">
            <span className="text-xs tracking-luxe uppercase text-[#6B7280]">{t("total")}</span>
            <span data-testid="checkout-total" className="font-serif-display text-2xl text-[#1A1F2C]">{format(total)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
